import { callable } from './callable.js'
import { uploadWorkspaceFiles } from './storageHubService.js'
import type { NlsKey } from '../nls/strings_nls.js'
import type { TimelineElement } from '../context/TimelineContext'

function isDescendantOf(
  el: TimelineElement,
  rootId: string,
  elementsById: Map<string, TimelineElement>,
): boolean {
  let curr: TimelineElement | undefined = el
  while (curr?.parentId) {
    if (curr.parentId === rootId) return true
    curr = elementsById.get(curr.parentId)
  }
  return false
}

export function canManageEduEvent(
  event: Pick<EduEventItem, 'audience'>,
  context: {
    isAdmin: boolean
    isOwner: boolean
    uid: string
    elements: TimelineElement[]
  },
): boolean {
  if (context.isOwner || context.isAdmin) {
    return true
  }

  const audience = event.audience
  if (!audience) return false

  if (audience.kind === 'ALL') {
    return false
  }

  if (audience.kind === 'NODES') {
    const nodeIds = new Set(audience.nodeIds || [])
    return context.elements.some(
      (el) => nodeIds.has(el.id) && Boolean(el.edu?.teachers?.[context.uid]),
    )
  }

  if (audience.kind === 'ROOT') {
    const rootId = audience.rootId
    if (!rootId) return false
    const elementsById = new Map(context.elements.map((el) => [el.id, el]))
    return context.elements.some((el) => {
      const isTarget = el.id === rootId || isDescendantOf(el, rootId, elementsById)
      return isTarget && Boolean(el.edu?.teachers?.[context.uid])
    })
  }

  return false
}

export type EduEventAudienceKind = 'ALL' | 'ROOT' | 'NODES'

export interface EduEventAudience {
  kind: EduEventAudienceKind
  rootId?: string | null
  nodeIds?: string[]
}

export interface EduEventAttachment {
  fileId: string
  name: string
  mimeType: string
  sizeBytes: number
}

export interface EduEventRsvpCounts {
  going: number
  notGoing: number
  maybe: number
  total: number
}

export type EduEventStatus = 'ACTIVE' | 'CANCELLED'

export interface EduEventItem {
  id: string
  workspaceId: string
  module: 'teaching' | 'training'
  audience: EduEventAudience
  audienceNodeIds: string[]
  title: string
  description?: string
  location?: string
  onlineUrl?: string
  allDay: boolean
  startAt: string // ISO string
  endAt: string   // ISO string
  timezone: string
  attachments: EduEventAttachment[]
  rsvpEnabled: boolean
  rsvpDeadline?: string | null // ISO string
  rsvpCounts: EduEventRsvpCounts
  reminderOffsetsMin: number[]
  status: EduEventStatus
  cancellationReason?: string | null
  cancelledAt?: string | null
  cancelledBy?: string | null
  createdBy: string
  createdAt: string
  updatedAt: string
  version: number
}

export interface EduEventRsvpItem {
  id: string
  workspaceId: string
  eventId: string
  learnerId: string
  learnerName?: string
  status: 'GOING' | 'NOT_GOING' | 'MAYBE'
  respondedByUid: string
  respondedByName?: string
  respondedAt: string
  createdAt: string
  updatedAt: string
}

export interface FamilyEduEventItem {
  id: string
  title: string
  description?: string
  location?: string
  onlineUrl?: string
  allDay: boolean
  startAt: string
  endAt: string
  timezone: string
  attachments: EduEventAttachment[]
  rsvpEnabled: boolean
  rsvpDeadline?: string | null
  rsvpCounts?: EduEventRsvpCounts
  status: EduEventStatus
  cancelReason?: string | null
  myRsvp?: 'GOING' | 'NOT_GOING' | 'MAYBE' | null
  learnerId?: string
  module?: 'teaching' | 'training'
  audience?: EduEventAudience
}

// ---------------------------------------------------------------------------
// Callables Input Types
// ---------------------------------------------------------------------------

export interface CreateEduEventInput {
  workspaceId: string
  module?: 'teaching' | 'training'
  audience: EduEventAudience
  title: string
  description?: string
  location?: string
  onlineUrl?: string
  allDay: boolean
  startAt: string
  endAt?: string
  timezone?: string
  fileIds?: string[]
  rsvpEnabled?: boolean
  rsvpDeadline?: string | null
  reminderOffsetsMin?: number[]
  requestId: string
}

export interface UpdateEduEventInput {
  workspaceId: string
  eventId: string
  title?: string
  description?: string
  location?: string
  onlineUrl?: string
  allDay?: boolean
  startAt?: string
  endAt?: string
  timezone?: string
  fileIds?: string[]
  rsvpEnabled?: boolean
  rsvpDeadline?: string | null
  reminderOffsetsMin?: number[]
  status?: 'ACTIVE' | 'CANCELLED'
  expectedVersion?: number
}

export interface CancelEduEventInput {
  workspaceId: string
  eventId: string
  cancellationReason: string
}

export interface DeleteEduEventInput {
  workspaceId: string
  eventId: string
}

export interface ListEduEventsInput {
  workspaceId: string
  module?: 'teaching' | 'training'
  audienceKind?: EduEventAudienceKind
  nodeId?: string
  from?: string
  to?: string
  limit?: number
  cursor?: string
}

export interface GetEduEventInput {
  workspaceId: string
  eventId: string
}

export interface GetEduEventRsvpsInput {
  workspaceId: string
  eventId: string
  cursor?: string
  limit?: number
}

export interface ListFamilyEduEventsInput {
  workspaceId: string
  from: string
  to: string
  learnerId?: string
  cursor?: string
  limit?: number
}

export interface GetFamilyEduEventInput {
  workspaceId: string
  learnerId: string
  eventId: string
}

export interface SetEduEventRsvpInput {
  workspaceId: string
  eventId: string
  learnerId: string
  response?: 'GOING' | 'NOT_GOING' | 'MAYBE'
  status?: 'GOING' | 'NOT_GOING' | 'MAYBE'
}

// ---------------------------------------------------------------------------
// Callables API
// ---------------------------------------------------------------------------

export async function createEduEvent(
  input: CreateEduEventInput,
): Promise<{ event: EduEventItem; created: boolean }> {
  return callable('createEduEvent', input)
}

export async function updateEduEvent(
  input: UpdateEduEventInput,
): Promise<{ event: EduEventItem; updated: boolean }> {
  return callable('updateEduEvent', input)
}

export async function cancelEduEvent(
  input: CancelEduEventInput,
): Promise<{ event: EduEventItem; cancelled: boolean }> {
  return callable('cancelEduEvent', input)
}

export async function deleteEduEvent(
  input: DeleteEduEventInput,
): Promise<{ success: boolean }> {
  return callable('deleteEduEvent', input)
}

export async function listEduEvents(
  input: ListEduEventsInput,
): Promise<{ events: EduEventItem[]; nextCursor: string | null }> {
  return callable('listEduEvents', input)
}

export async function getEduEvent(
  input: GetEduEventInput,
): Promise<{ event: EduEventItem }> {
  return callable('getEduEvent', input)
}

export async function getEduEventRsvps(
  input: GetEduEventRsvpsInput,
): Promise<{ rsvps: EduEventRsvpItem[]; nextCursor: string | null }> {
  return callable('getEduEventRsvps', input)
}

export async function listFamilyEduEvents(
  input: ListFamilyEduEventsInput,
): Promise<{ events: FamilyEduEventItem[]; nextCursor?: string | null }> {
  return callable('listFamilyEduEvents', input)
}

export async function getFamilyEduEvent(
  input: GetFamilyEduEventInput,
): Promise<{ event: FamilyEduEventItem }> {
  return callable('getFamilyEduEvent', input)
}

export async function setEduEventRsvp(
  input: SetEduEventRsvpInput,
): Promise<{ success?: boolean; response?: string; rsvp?: EduEventRsvpItem; rsvpCounts?: EduEventRsvpCounts }> {
  return callable('setEduEventRsvp', input)
}

// ---------------------------------------------------------------------------
// Request ID Helper (mirrors src/services/eduAnnouncementService.ts:105)
// ---------------------------------------------------------------------------

export function generateEduRequestId(prefix: string = 'req'): string {
  const rand = Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10)
  return `${prefix}_${Date.now()}_${rand}`.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64)
}

// ---------------------------------------------------------------------------
// File Upload Helper (mirrors src/services/eduFeedService.ts:178-200)
// ---------------------------------------------------------------------------

export interface UploadEduEventFilesInput {
  workspaceId: string
  requestId: string
  files: File[]
  onProgress?: (completed: number, total: number, fileName: string) => void
}

export async function uploadEduEventFiles(
  input: UploadEduEventFilesInput,
): Promise<string[]> {
  if (input.files.length === 0) return []
  for (const f of input.files) {
    const isImage = f.type.startsWith('image/')
    const isVideo = f.type.startsWith('video/')
    const isPdf = f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')
    if (!isImage && !isVideo && !isPdf) {
      throw new Error('Only image, video, and PDF files are supported for events.')
    }
  }
  const results = await uploadWorkspaceFiles({
    workspaceId: input.workspaceId,
    files: input.files,
    visibility: 'PRIVATE',
    artifactType: 'EDU_EVENT',
    artifactId: input.requestId,
    onProgress: input.onProgress,
  })
  const fileIds: string[] = []
  for (const res of results) {
    if (!res.fileId) {
      throw new Error('Upload completed without returning a file ID.')
    }
    fileIds.push(res.fileId)
  }
  return fileIds
}

// ---------------------------------------------------------------------------
// Error Mapping
// ---------------------------------------------------------------------------

export const EDU_EVENT_ERROR_CODE_MAP: Record<string, NlsKey> = {
  ADMIN_ONLY: 'NLSEduEventErrorAdminOnly',
  NODE_REQUIRED: 'NLSEduEventErrorNodeRequired',
  USE_CANCEL: 'NLSEduEventErrorUseCancel',
  HAS_RSVPS: 'NLSEduEventErrorHasRsvps',
  EVENT_TOO_LONG: 'NLSEduEventErrorEventTooLong',
  RANGE_TOO_LONG: 'NLSEduEventErrorRangeTooLong',
  RSVP_DEADLINE_AFTER_START: 'NLSEduEventErrorRsvpDeadlineAfterStart',
  'invalid onlineUrl': 'NLSEduEventErrorInvalidOnlineUrl',
  'permission-denied': 'NLSEduEventErrorPermissionDenied',
  RSVP_DEADLINE_PASSED: 'NLSEduEventErrorRsvpDeadlinePassed',
  EVENT_CANCELLED: 'NLSEduEventErrorEventCancelled',
  RSVP_DISABLED: 'NLSEduEventErrorRsvpDisabled',
  NOT_IN_AUDIENCE: 'NLSEduEventErrorNotInAudience',
  MINOR_READ_ONLY: 'NLSEduEventErrorMinorReadOnly',
}

export function mapEduEventError(error: unknown): NlsKey {
  if (!error) return 'NLSEduEventErrorGeneric'

  const errObj = typeof error === 'object' && error !== null ? (error as Record<string, unknown>) : null
  const details = typeof errObj?.details === 'object' && errObj?.details !== null ? (errObj.details as Record<string, unknown>) : null

  const code =
    typeof errObj?.code === 'string'
      ? errObj.code
      : typeof details?.code === 'string'
        ? details.code
        : ''

  if (code && EDU_EVENT_ERROR_CODE_MAP[code]) {
    return EDU_EVENT_ERROR_CODE_MAP[code]
  }

  const message = typeof errObj?.message === 'string' ? errObj.message : String(error)

  for (const [key, nlsKey] of Object.entries(EDU_EVENT_ERROR_CODE_MAP)) {
    if (message.includes(key)) {
      return nlsKey
    }
  }

  return 'NLSEduEventErrorGeneric'
}
